import { Link } from "react-router-dom";

import { useRecentEvents } from "../api/hooks";
import type { MigrationEvent, MigrationState } from "../api/types";
import { Card } from "../components";
import { t, tDynamic } from "../i18n/t";
import { failureReason, retryTarget } from "./LiveActivity";

const STOPPED: ReadonlySet<string> = new Set(["failed", "cancelled", "rolling_back", "rolled_back"]);

function destinationErrors(events: readonly MigrationEvent[]): string[] {
  const newest = events[0];
  if (newest === undefined || newest.to_state !== "failed") return [];
  const payload = (newest as { payload?: unknown }).payload;
  const errors =
    typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>).errors : undefined;
  return Array.isArray(errors) ? errors.filter((value): value is string => typeof value === "string") : [];
}

/**
 * xmg-webux (first staging trial, defect 4): what a sign-off step shows when it is NOT waiting for a
 * sign-off. A failed dry run used to keep the whole "Sign off the conversion" card (with a live destructive
 * button) under a "shown read-only" banner; the operator needs the opposite -- what happened, and what
 * happens next. `before` is the copy for a step not reached yet (sign-off opens later); a step left behind
 * normally (signed off, now loading) points at the status page.
 */
export function StepOutcome({
  id,
  state,
  title,
  before,
}: {
  id: string;
  state: MigrationState;
  title: string;
  /** The step has not been reached yet (its sign-off is still to come). */
  before: boolean;
}): JSX.Element {
  const recent = useRecentEvents(id === "" ? null : id, state);
  const events = recent.data ?? [];
  const statusLink = (
    <Link className="backlink" to={`/migrations/${id}/status`}>
      {t("outcome.open_status")}
    </Link>
  );

  if (!STOPPED.has(state)) {
    return (
      <Card title={title}>
        <p className="asidenote" data-testid="step-outcome">
          {before ? t("outcome.not_yet") : t("outcome.moved_on", { state: tDynamic("state.", state) })}
        </p>
        {before ? null : statusLink}
      </Card>
    );
  }

  const reason = failureReason(events);
  const errors = destinationErrors(events).slice(0, 5);
  const target = retryTarget(events);
  return (
    <Card title={t("outcome.title")}>
      <div data-testid="step-outcome">
        <p data-testid="step-outcome-what">
          {state === "failed"
            ? reason === null
              ? t("outcome.failed_no_reason")
              : t("outcome.failed", { reason })
            : t("outcome.stopped", { state: tDynamic("state.", state) })}
        </p>
        {errors.length === 0 ? null : (
          <ul className="stepoutcome__errors" aria-label={t("outcome.errors")}>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
        <h3>{t("outcome.next_title")}</h3>
        <p data-testid="step-outcome-next">
          {state === "failed"
            ? target === null
              ? t("outcome.next_retry")
              : t("outcome.next_retry_from", { state: tDynamic("state.", target) })
            : t("outcome.next_none")}
        </p>
        {statusLink}
      </div>
    </Card>
  );
}
